import { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from '@/components/shared/data-table/data-table-column-header'
import { OrderStatusBadge } from '@/components/shared/OrderStatusBadge'
import { OrderStatus, OrderItem } from '@/types/order'
import { calculateOrderVolume } from '@/utils/orderUtils'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Icons } from '@/components/shared/icons'
import { useDemoMode } from '@/context/DemoModeContext'

import { Order as BaseOrder, Customer } from '@/types/order'

export interface Order extends BaseOrder {
  company_name: string
  total_price: number
  purchased_by: Customer
}

export const columns: ColumnDef<Order>[] = [
  {
    accessorKey: 'createdAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.getValue('createdAt'))
      const formattedDate = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      const formattedTime = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      })
      return (
        <div className="flex flex-col text-xs">
          <div>{formattedDate}</div>
          <div className="text-xs text-muted-foreground">{formattedTime}</div>
        </div>
      )
    },
  },
  {
    accessorKey: 'confirmed_delivery_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Confirmed Delivery" />
    ),
    cell: ({ row }) => {
      const dateValue = row.getValue('confirmed_delivery_date')
      if (!dateValue)
        return <div className="text-xs text-muted-foreground">-</div>

      const formattedDate = new Date(dateValue as string).toLocaleDateString(
        'en-GB',
        {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }
      )
      return (
        <div className="flex flex-col text-xs">
          <div>{formattedDate}</div>
        </div>
      )
    },
  },
  {
    accessorKey: 'order_code',
    header: ({ column }) => <div className="text-xs">{'Order Code'} </div>,
    enableSorting: false,
  },
  {
    accessorKey: 'order_external_code',
    header: ({ column }) => <div className="text-xs">Sale Order</div>,
    enableSorting: false,
  },
  // {
  //   accessorKey: "customer_name",
  //   header: ({ column }) => (
  //     <DataTableColumnHeader column={column} title="Customer Name" />
  //   ),
  //   cell: ({ row }) => {
  //     const purchased_by = row.original.purchased_by;
  //     return (
  //       <div className="text-xs">{`${purchased_by.name} ${purchased_by.surname}`}</div>
  //     );
  //   },
  //   enableSorting: false,
  // },
  {
    accessorKey: 'company_name',
    header: ({ column }) => <div className="text-xs">Company Name</div>,
    cell: ({ row }) => {
      const { isDemoMode } = useDemoMode()
      return (
        <div className={isDemoMode ? 'blur-sm select-none' : ''}>
          {row.getValue('company_name')}
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'order_status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Order Status" />
    ),
    cell: ({ row }) => {
      const status = row.getValue('order_status') as OrderStatus
      return <OrderStatusBadge status={status} />
    },
  },
  {
    accessorKey: 'order_type',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Order Type" />
    ),
    cell: ({ row }) => {
      const status = row.getValue('order_type') as string
      return status && <Badge variant="outline">{status}</Badge>
    },
    enableSorting: false,
  },
  {
    accessorKey: 'order_items',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Volume" />
    ),
    cell: ({ row }) => {
      const volume = calculateOrderVolume(row.original)
      return <div className="text-xs">{`${volume.toFixed(2)} m³`}</div>
    },
    enableSorting: false,
  },

  {
    accessorKey: 'total_price',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Order Value"
        align="right"
      />
    ),
    cell: ({ row }) => {
      const { isDemoMode } = useDemoMode()
      const amount = parseFloat(row.getValue('total_price'))
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EUR',
      }).format(amount)
      return (
        <div className={`text-right ${isDemoMode ? 'blur-sm select-none' : ''}`}>
          {formatted}
        </div>
      )
    },
    size: 100,
  },
  {
    accessorKey: 'total_price_confirmed',
    header: ({ column }) => (
      <div className="flex items-center justify-end space-x-1">
        <DataTableColumnHeader
          column={column}
          title="Confirmed"
          align="right"
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Icons.help className="h-3 w-3 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Order Confirmed Value</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue('total_price_confirmed')) || 0
      const formatted = amount
        ? new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR',
          }).format(amount)
        : ''
      return <div className="text-right">{formatted}</div>
    },
    size: 100,
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const navigate = useNavigate()
      return (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/orders/${row.original.id}`)}
          >
            View details
          </Button>
        </div>
      )
    },
  },
]
